import React from 'react';
import { Box } from '@mui/material';

export const ARIA_BOT_SENDER_ID = 'aria-bot';

const SUPPORT_OUTBOUND_SOURCES = new Set(['partner', 'bot', 'admin', 'system']);
const SUPPORT_OUTBOUND_ROLES = new Set(['partner', 'admin']);

/** True when a chat message was sent by the Aria bot user. */
export const isAriaBotMessage = (message) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  if (message.sender?.isAriaBot === true || message.isAriaBot === true) {
    return true;
  }

  const candidates = [
    message.senderUid,
    message.senderId,
    message.sender?.uid,
    message.sender?.id,
    message.user?.id,
  ];

  return candidates.some(
    (value) =>
      String(value ?? '')
        .trim()
        .toLowerCase() === ARIA_BOT_SENDER_ID,
  );
};

/**
 * Aria / support inbox alignment:
 * outbound (right) = admin, partner staff replies
 * inbound (left) = member / end user / Aria bot
 */
export const isSupportOutboundMessage = (message, { firebaseUid } = {}) => {
  if (!message || typeof message !== 'object') {
    return false;
  }

  if (isAriaBotMessage(message)) {
    return false;
  }

  const source = String(message.source ?? message.sender?.source ?? '')
    .trim()
    .toLowerCase();
  if (source === 'aria') {
    return false;
  }
  if (SUPPORT_OUTBOUND_SOURCES.has(source)) {
    return true;
  }

  const role = String(message.sender?.role ?? message.senderRole ?? '')
    .trim()
    .toLowerCase();
  if (SUPPORT_OUTBOUND_ROLES.has(role)) {
    return true;
  }

  const senderType = String(message.senderType ?? message.sender?.type ?? '')
    .trim()
    .toLowerCase();
  if (SUPPORT_OUTBOUND_ROLES.has(senderType)) {
    return true;
  }

  if (message.isPartnerSender === true) {
    return true;
  }

  if (firebaseUid) {
    const senderUid = message.senderUid ?? message.sender?.uid ?? null;
    if (senderUid && String(senderUid) === String(firebaseUid)) {
      return true;
    }
  }

  return false;
};

/** Fallback avatar when Aria bot has no avatarUrl. */
const AriaBotAvatar = ({ size = 36, alt = 'Aria' }) => {
  const iconSize = Math.round(size * 0.5);

  return (
    <Box
      role="img"
      aria-label={alt}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: 'rgb(42, 26, 24)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        component="svg"
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ display: 'block' }}
      >
        <circle cx="12" cy="12" r="9" stroke="#F08080" strokeWidth="1.7" />
        <circle cx="12" cy="12" r="2.4" fill="#F08080" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3" stroke="#F08080" strokeWidth="1.7" />
      </Box>
    </Box>
  );
};

export default AriaBotAvatar;
